import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { useCallback, useMemo, memo } from 'react';
import UICheckbox from 'UIComponents/input/UICheckbox';
import { useTableDispatch, useTableSelector } from '../../redux/reduxHooks';
import { getSelectedRowIds, setRowsSelected } from '../redux/selectionSlice';
import styled from 'styled-components';
import { TABLE_BORDER_COLOR } from 'HubStyleTokens/theme';
import UIFlex from 'UIComponents/layout/UIFlex';
import { CELL_BORDER_WIDTH, SELECTION_COLUMN_WIDTH } from '../../table/constants/sizes'; // We cannot set legit borders because they do not work with sticky elements.
// Instead, we can create this div and position it one pixel outside its parent to simulate a border.
// We use this technique for the ResizeAffordance of the standard cells.

const RightBorder = styled.div.withConfig({
  displayName: "SelectAllCheckboxHeader__RightBorder",
  componentId: "lz32ni-0"
})(["border-right:1px solid ", ";height:100%;width:0;position:absolute;left:", "px;top:0;"], TABLE_BORDER_COLOR, SELECTION_COLUMN_WIDTH);
const StickyRightBorder = styled(RightBorder).withConfig({
  displayName: "SelectAllCheckboxHeader__StickyRightBorder",
  componentId: "lz32ni-1"
})(["position:sticky;"]);
/* Using borders on a position: sticky element does not work, so we
 * set the border using an inset box-shadow instead.
 */

const CheckboxTH = styled.th.withConfig({
  displayName: "SelectAllCheckboxHeader__CheckboxTH",
  componentId: "lz32ni-2"
})(["background-color:", ";box-shadow:inset 0 -", "px ", ";width:", "px;"], TABLE_BORDER_COLOR, CELL_BORDER_WIDTH, TABLE_BORDER_COLOR, SELECTION_COLUMN_WIDTH);
const StickyCheckboxTH = styled(CheckboxTH).withConfig({
  displayName: "SelectAllCheckboxHeader__StickyCheckboxTH",
  componentId: "lz32ni-3"
})(["left:0;position:sticky;z-index:3;"]);
const FullUIFlex = styled(UIFlex).withConfig({
  displayName: "SelectAllCheckboxHeader__FullUIFlex",
  componentId: "lz32ni-4"
})(["width:100%;height:100%;"]);

const SelectAllCheckboxHeader = ({
  numberOfFrozenColumns = 0,
  rowIds
}) => {
  const selectedRowIds = useTableSelector(getSelectedRowIds);
  const isCurrentPageSelected = useMemo(() => rowIds.length > 0 && rowIds.every(rowId => selectedRowIds.has(rowId)), [rowIds, selectedRowIds]);
  const dispatch = useTableDispatch();
  const handleChange = useCallback(({
    target: {
      checked
    }
  }) => {
    dispatch(setRowsSelected(rowIds.map(rowId => ({
      rowId,
      selected: checked
    }))));
  }, [dispatch, rowIds]);
  const Component = numberOfFrozenColumns > 0 ? StickyCheckboxTH : CheckboxTH;
  const Border = numberOfFrozenColumns > 0 ? StickyRightBorder : RightBorder;
  return /*#__PURE__*/_jsx(Component, {
    className: "p-all-0",
    "data-test-id": "checkbox-select-all-container",
    children: /*#__PURE__*/_jsxs(FullUIFlex, {
      align: "center",
      justify: "center",
      children: [/*#__PURE__*/_jsx(UICheckbox, {
        "data-test-id": "checkbox-select-all",
        checked: isCurrentPageSelected,
        indeterminate: selectedRowIds.size > 0,
        onChange: handleChange
      }), /*#__PURE__*/_jsx(Border, {})]
    })
  });
};

export default /*#__PURE__*/memo(SelectAllCheckboxHeader);